import {ReactNode, useEffect, useState} from 'react'

/*
 * A wrapper that ensures modals only render when the props are defined, while still preserving the closing animation.
 *
 * - If props are missing, nothing is rendered at all.
 * - Once opened with valid props, they are "mounted" and kept in state until the closing animation finishes.
 */
type ModalWrapperProps<Props> = {
  isVisible: boolean
  props: Props | null
  children: (props: Props & {isVisible: boolean; onExitComplete?: () => void}) => ReactNode
}

export function ModalWrapper<Props>({isVisible, props, children}: ModalWrapperProps<Props>) {
  // Keep a copy of the props while the modal is mounted,
  // so they persist during the closing animation.
  const [mountedProps, setMountedProps] = useState<Props | null>(null)

  useEffect(() => {
    if (isVisible && props) {
      setMountedProps(props)
    }
  }, [isVisible, props])

  // Nothing to render until we have props
  if (!mountedProps) return null

  const handleExitComplete = () => {
    // Once animation finishes and modal is closed, clear props
    if (!isVisible) {
      setMountedProps(null)
    }
  }

  return children({...mountedProps, isVisible, onExitComplete: handleExitComplete})
}
